import { updateProject } from "@/components/common/project-provider/update-project";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { useAppDispatch } from "@/store/store-hooks";
import { Tag } from "@/store/tags/tags-slice";
import { FilterMenuOptionType } from "@faro-lotv/flat-ui";
import { IElement } from "@faro-lotv/ielement-types";
import {
  createMutationAddLabel,
  createMutationRemoveLabel,
  Mutation,
  useApiClientContext,
} from "@faro-lotv/service-wires";
import { useCallback } from "react";

type AssignTagsToIElementCallback = (
  /** The element to assign tags to */
  iElement: IElement,
  /** The list of tags that are selected by the user */
  selectedTags: Tag[],
  /** The list of tags that can be assigned by the user. Only these tags will be removed, if they are not selected by the user. */
  selectableTags: FilterMenuOptionType[],
) => Promise<void>;

/**
 * @returns An asynchronous callback to update an iElements tag to match a list of selected tags.
 */
export function useAssignTagsToIElement(): AssignTagsToIElementCallback {
  const { projectApiClient } = useApiClientContext();
  const dispatch = useAppDispatch();
  const { handleErrorWithToast } = useErrorHandlers();

  return useCallback(
    async (iElement, selectedTags, selectableTags) => {
      const mutations = createAssignTagsToIElementMutations(
        iElement,
        selectedTags,
        selectableTags,
      );

      if (!mutations.length) return;

      try {
        await projectApiClient.applyMutations(mutations);

        await dispatch(
          updateProject({
            projectApi: projectApiClient,
            iElementQuery: { ids: [iElement.id] },
          }),
        );
      } catch (error) {
        handleErrorWithToast({ title: "Could not assign tags", error });
      }
    },
    [projectApiClient, dispatch, handleErrorWithToast],
  );
}

/**
 * @returns the mutations to modify an IElement's tags to match a list of assigned tags, only changing assigneable tags.
 * @param iElement The element to modify the tags for
 * @param selectedTags The list of tags that should be assigned to the element
 * @param selectableTags The list of tags that can be assigned by the user.
 * Only these tags will be removed, if they are not selected by the user.
 */
export function createAssignTagsToIElementMutations(
  iElement: IElement,
  selectedTags: Tag[],
  selectableTags: FilterMenuOptionType[],
): Mutation[] {
  const mutations: Mutation[] = [];

  for (const selectedTag of selectedTags) {
    if (iElement.labels?.some((label) => label.id === selectedTag.id)) {
      // Skip tags that are already assigned to the element
      continue;
    }
    mutations.push(createMutationAddLabel(iElement.id, selectedTag.id));
  }

  for (const label of iElement.labels ?? []) {
    if (!selectableTags.some((tag) => tag.key === label.id)) {
      // Skip tags that are not selectable by the user
      continue;
    }

    if (!selectedTags.some((tag) => tag.id === label.id)) {
      mutations.push(createMutationRemoveLabel(iElement.id, label.id));
    }
  }

  return mutations;
}
